import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { FIRM, VALUES } from "../lib/constants";
import { Button } from "../components/ui/button";

const LEADERSHIP = [
  {
    name: "Mohamed Dek Adan Ahmed, FCCA",
    role: "Managing Partner — Audit",
    bio: "9+ years across audit and assurance, including international firm training. Lead engagement partner on regulated financial institutions.",
  },
  {
    name: "Bile Bashir Salad",
    role: "Partner — Tax & Advisory",
    bio: "Specialist in Somali direct & indirect tax, cross‑border structuring and transfer pricing for telecoms and trading groups.",
  },
  {
    name: "Shire Ukash Ali",
    role: "Partner — Risk & Internal Audit",
    bio: "Designs and reviews risk‑based internal audit programs and AML/CFT frameworks for banks and MFIs.",
  },
];

const COMMITMENTS = [
  "Independence and objectivity on every file",
  "ISA‑compliant methodology and quality control",
  "Senior involvement throughout the engagement",
  "Transparent fees and clearly scoped engagement letters",
  "Confidentiality protected by partner‑level governance",
];

export default function About() {
  return (
    <div data-testid="page-about">
      {/* Header */}
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 anim-fade-up">
            <div className="overline rule-teal">About the firm</div>
            <h1 className="font-display text-5xl sm:text-6xl mt-6 leading-[1.02] text-[#0B2E5C]">
              We are auditors first.
            </h1>
            <p className="mt-8 text-lg text-[#475569] leading-relaxed font-light max-w-2xl">
              Mizan Audit & Assurance LLP was founded to bring Big‑Four discipline
              to Somali businesses, NGOs and public bodies — without the overhead
              and without the distance. Independent, locally rooted, internationally
              trained.
            </p>
          </div>
          <div className="lg:col-span-5 anim-fade-up delay-2">
            <div className="border border-[#E2E8F0] p-8 bg-[#F8FAFC]">
              <div className="overline">At a glance</div>
              <dl className="mt-6 space-y-4 text-sm">
                <div className="flex justify-between gap-6 border-b border-[#E2E8F0] pb-3">
                  <dt className="text-[#475569]">Founded</dt>
                  <dd className="font-medium text-[#0B2E5C]">Mogadishu, Somalia</dd>
                </div>
                <div className="flex justify-between gap-6 border-b border-[#E2E8F0] pb-3">
                  <dt className="text-[#475569]">Headquarters</dt>
                  <dd className="font-medium text-[#0B2E5C] text-right">
                    Orphane Tower, 8F · 804
                  </dd>
                </div>
                <div className="flex justify-between gap-6 border-b border-[#E2E8F0] pb-3">
                  <dt className="text-[#475569]">Methodology</dt>
                  <dd className="font-medium text-[#0B2E5C]">ISA / IFRS / IPSAS</dd>
                </div>
                <div className="flex justify-between gap-6">
                  <dt className="text-[#475569]">Languages</dt>
                  <dd className="font-medium text-[#0B2E5C]">English · Somali · Arabic</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline rule-teal">Our story</div>
            <h2 className="font-display text-4xl mt-4 leading-tight text-[#0B2E5C]">
              The word <span className="italic text-[#2BA8B8]">Mizan</span> means balance.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[#475569] font-light leading-relaxed text-lg">
            <p>
              In Arabic, <em>mizan</em> is the scale used to weigh — and, by
              extension, the standard of fairness. We chose it because it captures
              what an audit is supposed to be: a balanced, independent judgement
              grounded in evidence.
            </p>
            <p>
              The firm was built by partners with international training who saw a
              gap in the Somali market — clients deserved more than a signature on
              a financial statement. They deserved an audit they could trust, and
              insight they could act on.
            </p>
            <p>
              Today, Mizan serves banks, MFIs, telecoms, INGOs, donor‑funded
              programs and the public sector — quietly delivering work that holds
              up under regulator, board and donor scrutiny.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white border-b border-[#E2E8F0]" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="overline rule-teal">Values</div>
          <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight text-[#0B2E5C] max-w-3xl">
            What we hold ourselves to.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px mt-14 bg-[#E2E8F0] border border-[#E2E8F0]">
            {VALUES.map((v, i) => (
              <div
                key={v.title}
                data-testid={`about-value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-white p-10 card-lift"
              >
                <div className="flex items-center gap-4">
                  <span className="font-display text-3xl text-[#2BA8B8]">
                    0{i + 1}
                  </span>
                  <h3 className="font-display text-2xl text-[#0B2E5C]">
                    {v.title}
                  </h3>
                </div>
                <p className="mt-5 text-[#475569] font-light leading-relaxed">
                  {v.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="bg-[#0B2E5C] text-white" data-testid="about-leadership">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="overline !text-[#2BA8B8] rule-teal">Leadership</div>
          <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight max-w-3xl">
            Senior partners on every engagement.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-14 bg-white/10">
            {LEADERSHIP.map((p) => (
              <div
                key={p.name}
                data-testid={`leader-${p.name.split(" ")[0].toLowerCase()}`}
                className="bg-[#0B2E5C] p-10 border border-white/0 hover:border-[#2BA8B8]/40 transition-colors"
              >
                <div className="h-16 w-16 rounded-full bg-[#2BA8B8]/20 flex items-center justify-center font-display text-2xl text-[#2BA8B8]">
                  {p.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                </div>
                <h3 className="font-display text-2xl mt-6">{p.name}</h3>
                <div className="text-sm text-[#2BA8B8] mt-1 font-medium">
                  {p.role}
                </div>
                <p className="mt-5 text-white/70 font-light leading-relaxed text-sm">
                  {p.bio}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitments */}
      <section className="bg-white" data-testid="about-commitments">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline rule-teal">Our commitments</div>
            <h2 className="font-display text-4xl mt-4 leading-tight text-[#0B2E5C]">
              What our clients can count on.
            </h2>
            <p className="mt-6 text-[#475569] font-light leading-relaxed">
              Every engagement letter we sign is backed by five non‑negotiables.
            </p>
            <Link to="/contact" data-testid="about-cta-contact" className="inline-block mt-8">
              <Button className="rounded-sm bg-[#0B2E5C] hover:bg-[#082142] text-white h-12 px-7">
                Meet our partners
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-5">
              {COMMITMENTS.map((c, i) => (
                <li
                  key={i}
                  className="flex items-start gap-4 pb-5 border-b border-[#E2E8F0]"
                  data-testid={`commitment-${i}`}
                >
                  <CheckCircle2 className="h-5 w-5 text-[#2BA8B8] mt-1 shrink-0" strokeWidth={1.5} />
                  <span className="text-[#0B2E5C] text-lg font-light">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
